import {
  CalendarFilled,
  CreditCardFilled,
  MoneyCollectFilled,
  SaveFilled,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Button, DatePicker, Input, Select } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import * as Yup from "yup";
import { useGetCategories, useGetCategoriesOnly } from "../../../api/api.get";
import { useMessage } from "../../../components/notifications/message";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { useAuthContext } from "../../../providers/auth.provider";
import { useCurrencyContext } from "../../../providers/currency.provider";
import { IExpense2 } from "../../../types/types";
import { useUpdateExpense } from "../../../api/api.update";

export default function ExpenseEditSection({
  onCancel,
  expense,
}: {
  expense?: IExpense2;
  onCancel: () => void;
}) {
  const navigate = useNavigate();
  const { currency } = useCurrencyContext();
  const { user } = useAuthContext();
  const { notification, messageError } = useMessage();

  const { mutateAsync: updateExpenseAsync } = useUpdateExpense(
    expense?.groupId,
    expense?.id,
  );

  const { data: categoryData } = useGetCategoriesOnly();
  const { data: categoryToSubCatMap } = useGetCategories();

  const categoryOptions = useMemo(() => {
    if (categoryData) {
      return categoryData.map((c: string) => {
        return { label: c, value: c };
      });
    }
    return [];
  }, [categoryData]);

  const formik = useFormik({
    initialValues: {
      category: expense?.category ?? "",
      subcategory: expense?.subcategory ?? "",
      amount: expense?.amount,
      comments: expense?.comments ?? "",
      expenseDate:
        dayjs(expense?.expenseDate).format("YYYY-MM-DD") ?? undefined,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      category: Yup.string().required("Please select category"),
      subcategory: Yup.string().required("Please select subcategory"),
      amount: Yup.string()
        .matches(/^(0|[1-9]\d*)(\.\d+)?$/, "Please enter valid number")
        .required("Please enter amount"),
      comments: Yup.string().optional(),
      expenseDate: Yup.string().required("Please select date"),
    }),
    onSubmit: (values) => {
      updateExpenseAsync({
        category: values.category,
        amount: values.amount ? +values.amount : 0,
        comments: values.comments,
        creditcard: "",
        expenseDate: values.expenseDate,
        subcategory: values.subcategory,
      })
        .then(() => onCancel())
        .catch(() => messageError("Whoops! Please try again."));
    },
  });

  const subcategoryOptions: any[] = useMemo(() => {
    if (categoryToSubCatMap) {
      const subcats = categoryToSubCatMap[formik.values.category] ?? [];

      if (subcats) {
        return subcats.map((c: string) => {
          return { label: c, value: c };
        });
      }
      return [];
    }
    return [];
  }, [categoryToSubCatMap, formik.values.category]);

  return (
    <div className="min-h-screen bg-slate-50">
      {notification}

      {/* Main Content */}
      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <form onSubmit={formik.handleSubmit}>
          <div className="max-w-4xl mx-auto">
            {/* Top Navigation */}
            <div className="mb-8 flex justify-between items-center">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-blue-500 text-sm hover:text-primary-dark underline"
              >
                Back
              </button>
              <div className="flex gap-3">
                <Button type="dashed" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<SaveFilled />}
                  disabled={
                    Object.keys(formik.errors).length > 0 ||
                    expense?.username !== user?.username
                  }
                  onClick={() => formik.handleSubmit()}
                >
                  {" "}
                  Save
                </Button>
              </div>
            </div>

            {Object.keys(formik.errors).length > 0 && (
              <p>
                {
                  (formik.errors as any)[
                    Object.keys(formik.errors).at(0) as any
                  ]
                }
              </p>
            )}

            {/* Main Header Card */}
            <Card className="bg-white mb-6 border-2">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2 flex flex-col gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2 font-bold">
                        Category
                      </p>
                      <Select
                        options={categoryOptions}
                        size="middle"
                        style={{ width: "100%", maxWidth: 240 }}
                        value={formik.values.category}
                        onChange={(value: string) => {
                          formik.setFieldValue("category", value);
                          formik.setFieldValue("subcategory", undefined);
                        }}
                      />
                      {formik.errors.category && (
                        <p className="text-xs text-muted-foreground mb-2 text-red-600">
                          Please select an option
                        </p>
                      )}
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground mb-2 font-bold">
                        Sub Category
                      </p>
                      <Select
                        options={subcategoryOptions}
                        size="large"
                        style={{ width: "100%", maxWidth: 240 }}
                        value={formik.values.subcategory}
                        onChange={(value: string) =>
                          formik.setFieldValue("subcategory", value)
                        }
                      />
                      {formik.errors.subcategory && (
                        <p className="text-xs text-muted-foreground mb-2 text-red-600">
                          Please select an option
                        </p>
                      )}
                    </div>

                    <div className="flex items-start flex-col gap-2 mt-10">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CalendarFilled className="w-4 h-4" />
                        <span>
                          <DatePicker
                            value={
                              formik.values.expenseDate
                                ? dayjs(formik.values.expenseDate)
                                : undefined
                            }
                            onChange={(date) =>
                              formik.setFieldValue(
                                "date",
                                date?.format("YYYY-MM-DD"),
                              )
                            }
                          />
                        </span>
                      </div>
                      {formik.errors.expenseDate && (
                        <p className="text-xs text-muted-foreground mb-2 text-red-600">
                          Please select date
                        </p>
                      )}
                      {expense?.type === "misc" && (
                        <div
                          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-amber-400 gap-2 flex`}
                        >
                          <MoneyCollectFilled />
                          <span>Misc</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Amount Box */}
                  <div className="bg-blue-50 rounded-xl p-4 sm:p-6 flex flex-col justify-center items-center text-center">
                    <p className="text-sm text-muted-foreground mb-2">Amount</p>

                    <div className="flex flex-row items-center justify-items-center text-center gap-2 w-full">
                      <Input
                        prefix={currency}
                        type={"number"}
                        style={{
                          fontSize: "clamp(20px, 5vw, 36px)",
                          width: "100%",
                          fontWeight: "bold",
                        }}
                        maxLength={6}
                        {...formik.getFieldProps("amount")}
                      />
                    </div>
                    {formik.errors.amount && (
                      <p className="text-xs text-muted-foreground mb-2 text-red-600">
                        {formik.errors.amount}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Three Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="lg:col-span-1 space-y-6">
                {/* Payment Method */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCardFilled className="w-5 h-5 text-primary" />
                      Payment Method
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                        Payment Channel
                      </p>
                      <p className="font-semibold text-foreground text-gray-600">
                        {expense?.paymentChannel ?? "-"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-1 space-y-6">
                {/* Merchant Info */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UserOutlined className="w-5 h-5 text-primary" />
                      Merchant
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                        Merchant Name
                      </p>

                      <p className="font-semibold text-foreground mb-4">
                        {expense?.merchantLogo && (
                          <Avatar src={expense?.merchantLogo} size={"small"} />
                        )}
                        {expense?.merchantName ?? "-"}
                      </p>
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        {expense?.location}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Notes Section */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent className="gap-4">
                <p className="text-foreground leading-relaxed">
                  {expense?.comments}
                </p>

                <Input.TextArea
                  size="large"
                  rows={4}
                  placeholder="Enter comments.... For eg. Bought items for family."
                  {...formik.getFieldProps("comments")}
                />
              </CardContent>
            </Card>
          </div>
        </form>
      </main>
    </div>
  );
}
